import { motion } from "framer-motion";
import { ArrowUpRight, MapPin } from "lucide-react";
import { Brief, Plan } from "./data";
import { money } from "./shared";
import { coverFor } from "./film";
import { cn } from "@/lib/utils";

/** One browseable plan card. Only what you need to choose. */
export default function PlanCard({
  plan,
  brief,
  index,
  onOpen,
}: {
  plan: Plan;
  brief: Brief;
  index: number;
  onOpen: () => void;
}) {
  const perPerson = Math.round(plan.total / Math.max(1, brief.teamSize));
  const overBudget = plan.total > brief.budgetTotal;

  const wash =
    plan.accent === "green"
      ? "bg-primary/[0.06]"
      : plan.accent === "lavender"
      ? "bg-secondary/20"
      : "bg-accent/10";

  return (
    <motion.button
      onClick={onOpen}
      initial={{ opacity: 0, y: 26 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.8, delay: index * 0.12, ease: [0.16, 1, 0.3, 1] }}
      className={cn(
        "group text-left rounded-2xl border border-border overflow-hidden flex flex-col",
        "hover:border-primary/50 transition-colors",
        wash
      )}
    >
      <div className="relative aspect-[4/5] overflow-hidden">
        <img
          src={coverFor(plan.id)}
          alt={plan.name}
          loading="lazy"
          width={1280}
          height={1600}
          className="h-full w-full object-cover film-tone transition-transform duration-[1400ms] ease-out group-hover:scale-[1.06]"
        />
        <div className="absolute inset-0 film-grain" />
        <div className="absolute inset-0 bg-gradient-to-t from-primary/70 via-transparent to-transparent" />
        <p className="absolute bottom-5 left-5 right-5 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-primary-foreground/90">
          <MapPin size={12} /> {plan.location.split(",")[0]}
        </p>
      </div>

      <div className="p-6 flex flex-col flex-1">
        <h3 className="font-funnel font-bold text-2xl text-foreground mb-3">{plan.name}</h3>
        <p className="text-sm text-foreground/80 mb-7">{plan.concept}</p>

        <div className="mt-auto space-y-5">
          <div className="flex items-end justify-between">
            <div>
              <p className="font-funnel font-bold text-2xl text-foreground">{money(plan.total)}</p>
              <p className="text-sm text-muted-foreground">{money(perPerson)} per person</p>
            </div>
            <span
              className={cn(
                "rounded-full px-3 py-1 text-[11px]",
                overBudget ? "bg-accent/25 text-foreground" : "bg-primary/10 text-foreground"
              )}
            >
              {overBudget ? "over budget" : "within budget"}
            </span>
          </div>

          <div>
            <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
              <span>travel fairness</span>
              <span className="text-foreground">{plan.travelLabel}</span>
            </div>
            <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
              <motion.div
                className="h-full bg-primary"
                initial={{ width: 0 }}
                whileInView={{ width: `${plan.travelFairness}%` }}
                viewport={{ once: true }}
                transition={{ duration: 1, delay: 0.2 + index * 0.1, ease: "easeOut" }}
              />
            </div>
          </div>

          <div className="flex items-center justify-between border-t border-border pt-5">
            <span className="text-xs text-muted-foreground">
              {plan.balance} productive / {100 - plan.balance} social
            </span>
            <span className="inline-flex items-center gap-1.5 text-sm text-foreground">
              see the day
              <ArrowUpRight
                size={15}
                className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </span>
          </div>
        </div>
      </div>
    </motion.button>
  );
}
